// components/common/table/CustomDropdown.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";
import { ChevronDown, ChevronUp, ChevronRight, Plus } from "lucide-react";
import { Button } from "../Button";
import Loader from "../Loader";
import { calculateSmartPosition, PositionInfo } from "@/utils/dropdownPositioning";
import { PositionDebugOverlay } from "@/components/common/PositionDebugOverlay";

interface CustomDropdownProps {
  value: string;
  options: {
    value: string;
    label: string;
    color?: string;
    bgColor?: string;
    borderColor?: string;
  }[];
  onChange: (value: string) => void;
  placeholder?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  autoFocus?: boolean;
  autoOpen?: boolean;
  onOptionHover?: (value: string, element: HTMLButtonElement | null, triggeredByKeyboard?: boolean) => void;
  showArrowOnOption?: string;
  dropdownWidth?: number;
  minDropdownWidth?: number;
  onAddClick?: () => void;
  addLabel?: string;
  showAddButton?: boolean;
  loading?: boolean;
  debug?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  options,
  onChange,
  placeholder = "Select",
  onKeyDown,
  onBlur,
  autoFocus = false,
  autoOpen = true,
  onOptionHover,
  showArrowOnOption,
  dropdownWidth = 220,
  minDropdownWidth = 180,
  onAddClick,
  showAddButton,
  addLabel,
  loading = false,
  debug = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    position: PositionInfo['position'];
  }>({
    top: 0,
    left: 0,
    width: 0,
    position: 'bottom-right',
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedOption = options.find((opt) => opt.value === value);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Create portal container
  useEffect(() => {
    const portalDiv = document.createElement("div");
    portalDiv.id = "dropdown-portal";
    document.body.appendChild(portalDiv);
    portalRef.current = portalDiv;

    return () => {
      if (portalRef.current && document.body.contains(portalRef.current)) {
        document.body.removeChild(portalRef.current);
      }
    };
  }, []);

  // Auto-focus and auto-open
  useEffect(() => {
    if (autoFocus && buttonRef.current) {
      const timer = setTimeout(() => {
        buttonRef.current?.focus();
        if (autoOpen) {
          setIsOpen(true);
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [autoFocus, autoOpen]);

  // Update dropdown position when opened - SMART POSITIONING
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      // Calculate dropdown height based on content
      const searchSectionHeight = (options.length > 2 || showAddButton) ? 60 : 0;
      const optionHeight = 32;
      const dropdownHeight = Math.min(
        280, 
        searchSectionHeight + (filteredOptions.length * optionHeight) + 16
      );
      
      // Use original width calculation
      const calculatedWidth = Math.max(
        minDropdownWidth,
        Math.min(dropdownWidth, dropdownWidth)
      );

      // Calculate smart position
      const smartPosition = calculateSmartPosition(
        buttonRef.current,
        calculatedWidth,
        dropdownHeight,
        {
          preferredPosition: 'bottom',
          offset: 2,
          debug: debug
        }
      );

      setDropdownPosition({
        top: smartPosition.top,
        left: smartPosition.left,
        width: calculatedWidth,
        position: smartPosition.position,
      });

      // Focus search input when dropdown opens
      if (options.length > 2 && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 50);
      }

      // Notify parent about dropdown position for sub-dropdowns
      if (onOptionHover) {
        // Store position in data attribute for sub-dropdowns to reference
        dropdownRef.current?.setAttribute('data-dropdown-position', smartPosition.position);
      }
    }
  }, [isOpen, options.length, filteredOptions.length, dropdownWidth, minDropdownWidth, showAddButton, debug]);

  // Rest of the original code remains the same...
  // Update selected index when value or filtered options change
  useEffect(() => {
    const idx = filteredOptions.findIndex((opt) => opt.value === value);
    if (idx >= 0) {
      setSelectedIndex(idx);
    } else {
      setSelectedIndex(0);
    }
  }, [value, filteredOptions]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setSearchLoading(false);
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle Escape key globally
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Scroll selected option into view
  useEffect(() => {
    if (isOpen && optionRefs.current[selectedIndex]) {
      optionRefs.current[selectedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [isOpen, selectedIndex]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setTimeout(() => {
      buttonRef.current?.focus();
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (isOpen && filteredOptions[selectedIndex]) {
          handleSelect(filteredOptions[selectedIndex].value);
        } else {
          setIsOpen(true);
        }
        break;

      case " ":
        e.preventDefault();
        if (isOpen && filteredOptions[selectedIndex]) {
          handleSelect(filteredOptions[selectedIndex].value);
        } else {
          setIsOpen(true);
        }
        break;

      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setSelectedIndex((prev) => {
            const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
            // Trigger hover callback for keyboard navigation
            setTimeout(() => triggerOptionHoverForKeyboard(newIndex), 0);
            return newIndex;
          });
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setSelectedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : 0;
            // Trigger hover callback for keyboard navigation
            setTimeout(() => triggerOptionHoverForKeyboard(newIndex), 0);
            return newIndex;
          });
        }
        break;

      case "Escape":
        e.preventDefault();
        if (isOpen) {
          setIsOpen(false);
        } else {
          onKeyDown?.(e);
        }
        break;

      case "Tab":
        if (isOpen) {
          e.preventDefault();
          if (filteredOptions[selectedIndex]) {
            handleSelect(filteredOptions[selectedIndex].value);
          }
          setTimeout(() => {
            onKeyDown?.(e);
          }, 50);
        } else {
          onKeyDown?.(e);
        }
        break;

      default:
        if (!isOpen && e.key.length === 1) {
          setIsOpen(true);
        }
        break;
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
          // Trigger hover callback for keyboard navigation
          setTimeout(() => triggerOptionHoverForKeyboard(newIndex), 0);
          return newIndex;
        });
        break;

      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : 0;
          // Trigger hover callback for keyboard navigation
          setTimeout(() => triggerOptionHoverForKeyboard(newIndex), 0);
          return newIndex;
        });
        break;

      case "Enter":
        e.preventDefault();
        if (filteredOptions[selectedIndex]) {
          handleSelect(filteredOptions[selectedIndex].value);
        }
        break;

      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;

      case "Tab":
        e.preventDefault();
        if (filteredOptions[selectedIndex]) {
          handleSelect(filteredOptions[selectedIndex].value);
        }
        setTimeout(() => {
          onKeyDown?.(e);
        }, 50);
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    setTimeout(() => {
      if (
        document.activeElement !== buttonRef.current &&
        !dropdownRef.current?.contains(document.activeElement)
      ) {
        onBlur?.(e);
      }
    }, 100);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Enhanced hover handler to pass position info
  const handleOptionMouseEnter = (index: number, option: any) => {
    setSelectedIndex(index);
    if (onOptionHover && optionRefs.current[index]) {
      // Pass dropdown position info for smart sub-dropdown positioning
      const element = optionRefs.current[index];
      if (element) {
        element.setAttribute('data-parent-position', dropdownPosition.position);
      }
      onOptionHover(option.value, element, false); // false = triggered by mouse
    }
  };

  // Helper to trigger onOptionHover for keyboard navigation
  const triggerOptionHoverForKeyboard = (index: number) => {
    if (onOptionHover && filteredOptions[index] && optionRefs.current[index]) {
      const element = optionRefs.current[index];
      if (element) {
        element.setAttribute('data-parent-position', dropdownPosition.position);
        onOptionHover(filteredOptions[index].value, element, true); // true = triggered by keyboard
      }
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={clsx(
          "w-full h-full  outline-none",
          "text-sm bg-white text-black",
          "flex items-center justify-between",
          "cursor-pointer transition-colors",
          // "hover:border-blue-600",
          !selectedOption && "text-gray-400",
        )}
        style={{
          height: "24px",
          minHeight: "24px",
          padding: "0 8px",
        }}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
               <span className="truncate flex-1 pr-2 text-sm text-left">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        {isOpen ? (
          <ChevronUp size={12} className="text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown size={12} className="text-gray-500 flex-shrink-0" />
        )}
      </button>

      {/* Debug Overlay */}
      {debug && isOpen && dropdownPosition && <PositionDebugOverlay positionInfo={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        position: dropdownPosition.position,
        debug: {
          availableSpace: { top: 0, right: 0, bottom: 0, left: 0 },
          elementSize: { width: dropdownPosition.width, height: 0 },
          triggerRect: buttonRef.current?.getBoundingClientRect() || new DOMRect(),
        }
      }} />}

      {isOpen &&
        portalRef.current &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className="bg-white shadow-lg z-[9999]"
            style={{
              position: "absolute",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: dropdownPosition.width,
              maxWidth: `min(${dropdownPosition.width}px, calc(100vw - 16px))`,
            }}
            role="listbox"
            onClick={(e) => e.stopPropagation()}
            data-dropdown-position={dropdownPosition.position}
          >
            {/* Top row: Search and Add button - EXACT MATCH to original */}
            {(options.length > 2 || showAddButton) && (
              <div className="flex items-center gap-2 mb-2 pt-2 px-2">
                {options.length > 2 && (
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={`Search ${addLabel?.toLowerCase() || 'items'}...`}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    className="flex-1 min-w-0 px-2 py-1 border rounded-lg text-xs w-full"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    autoFocus={false}
                  />
                )}
                {showAddButton && onAddClick && (
                  <Button
                    variant="primary"
                    size="xs"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAddClick();
                      setIsOpen(false);
                    }}
                    className={`flex-shrink-0 whitespace-nowrap h-6 ${
                      addLabel === "Add Status" ? "w-full" : ""
                    }`}
                  >
                    <Plus size={18} /> &nbsp; {addLabel}
                  </Button>
                )}
              </div>
            )}

            {/* List or loader - EXACT MATCH to original */}
            <div className="max-h-[240px] overflow-y-auto px-2 pb-2">
              {(loading || searchLoading) ? (
                <div className="flex justify-center items-center h-20">
                  <Loader />
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="text-center text-gray-500 text-xs py-4">
                  No items found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <button
                    key={option.value}
                    ref={(el) => (optionRefs.current[index] = el)}
                    type="button"
                    className={clsx(
                      "w-full flex items-center px-2 py-2 cursor-pointer hover:bg-gray-50 rounded select-none text-xs mx-1",
                      "focus:outline-none transition-colors",
                      index === selectedIndex && "bg-[#F5F5F5]",
                      option.value === value && "bg-[#F5F5F5] font-medium text-blue-800"
                    )}
                    style={{
                      ...(option.color && { color: option.color }),
                    }}
                    onClick={() => handleSelect(option.value)}
                    onMouseEnter={() => handleOptionMouseEnter(index, option)}
                    onMouseDown={(e) => e.preventDefault()}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    {/* Color dot for status - EXACT MATCH to original */}
                    {option.borderColor && (
                      <span
                        className="h-3 w-3 rounded-full flex-shrink-0 mr-2"
                        style={{ backgroundColor: option.borderColor }}
                      />
                    )}
                    <span className="truncate text-left flex-1">
                      {option.label}
                    </span>
                    {showArrowOnOption === option.value && (
                      <ChevronRight
                        size={14}
                        className="text-gray-400 flex-shrink-0 ml-2"
                      />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>,
          portalRef.current
        )}
    </>
  );
};

export default CustomDropdown;